import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Eyebrow, FadeUp } from "./Reveal";
import { setJsonLd } from "../lib/useSEO";

export const FAQS = [
  {
    q: "Is HiPlate food 100% halal?",
    a: "Yes. Every protein we cook is certified halal, and our kitchen is halal-only — no pork, no alcohol, no cross-contamination. That applies to both weekly meal prep and all catering.",
  },
  {
    q: "Where do you deliver halal meal prep?",
    a: "We're based in Woodbridge, VA and deliver across the entire DMV: Washington DC, Maryland (Montgomery and Prince George's counties and nearby), and Northern Virginia (Fairfax, Arlington, Alexandria, Prince William, Loudoun). Delivery only — there's no pickup.",
  },
  {
    q: "Why is there a 5-meal minimum?",
    a: "Each weekly bundle starts at 5 meals so we can cook in small, fresh batches and keep delivery affordable. Mix any dishes you like — 5 different meals, 5 of the same, or any combination. Add sides and desserts on top.",
  },
  {
    q: "When do meals arrive and how long do they keep?",
    a: "Meals are cooked fresh and delivered chilled in sealed, microwave-safe containers. They keep 4–5 days in the fridge, and most reheat in about 2 minutes. We text or call before your delivery window.",
  },
  {
    q: "Do you cater corporate events, parties and weddings?",
    a: "Yes — from drop-off office lunches to full-service plated weddings with live stations and staff. Use the catering estimator for an instant per-guest price, send an inquiry (you can attach mood-board photos), and our events team replies within one business day.",
  },
  {
    q: "How far in advance should I book catering?",
    a: "Corporate drop-off: 48 hours is usually enough. Parties and grazing tables: about one week. Weddings and events over 100 guests: 3–6 weeks, which leaves time for a tasting.",
  },
  {
    q: "Can you accommodate allergies and dietary needs?",
    a: "Absolutely. Every dish lists its ingredients and macros, and we offer high-protein, low-carb, plant-forward and gluten-free options. For severe allergies, add a note at checkout or mention it in your catering inquiry and we'll adjust.",
  },
  {
    q: "How do I pay?",
    a: "Meal prep orders are paid securely online by card at checkout (powered by Stripe). First order? Use code HIPLATE15 for 15% off. Catering is quoted first, then invoiced.",
  },
];

export const FAQ = () => {
  useEffect(() => {
    setJsonLd("faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
  }, []);

  return (
    <section className="bg-cream py-20 lg:py-28" data-testid="faq">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-[1fr_1.6fr]">
        <FadeUp>
          <Eyebrow>Good to know</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Halal meal prep &amp; catering, <span className="italic text-spice">answered.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/70 sm:text-base">
            Everything people ask before their first HiPlate order — from certification to delivery zones. Still curious?
          </p>
          <Link to="/contact" data-testid="faq-contact-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-spice transition-colors hover:text-ink">
            Ask the kitchen <ArrowRight size={15} />
          </Link>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Accordion type="single" collapsible className="divide-y divide-inkline rounded-3xl border border-inkline bg-parchment px-6 sm:px-8" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-ink hover:no-underline sm:text-lg" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-ink/75 sm:text-base" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </div>
    </section>
  );
};
